import { IconButton } from "@mui/material";
import { useTheme } from "@mui/styles";
import {
  QUERY_PROGRESS_PENDING,
  REQUEST_ACTION_PATIENT_PREFERRED,
  SEARCH_TABLE_CONTACTED,
  SEARCH_TABLE_POTENTIAL,
} from "core/consts";
import { useSafeState } from "core/hooks";
import { isPrintMode } from "core/model/utils/urls";
import {
  AcceptedTableRequest,
  Auction,
  AuctionRequest,
  ContactedTableRequest,
  DeclinedTableRequest,
  PotentialReceiversTableCandidate,
  QueryProgress,
  RejectedTableRequest,
  SearchTable,
  ValidatedTableRequest,
} from "core/types";
import { TextInputField } from "ds_legacy/components/TextInputField";
import Tooltip from "ds_legacy/components/Tooltip";
import {
  ICON_GREY,
  SUCCESS_COLOR,
  UNAVAILABLE_GREY,
  WHITE,
} from "ds_legacy/materials/colors";
import { HorizontalLayout, VerticalLayout } from "ds_legacy/materials/layouts";
import { dp, margin, padding } from "ds_legacy/materials/metrics";
import { FONT_SIZE_10, FONT_SIZE_16 } from "ds_legacy/materials/typography";
import { usePotentialReceiverContext } from "dsl/ecosystems/PatientSearchMerge/Components/SearchPageTables/Tables/PotentialReceiverTable/PotentialReceiverContext";
import { shouldDisableActions } from "dsl/ecosystems/PatientSearchMerge/Components/Shared/utils";
import { useRequestAction } from "dsl/hooks";
import { CheckIcon, LockOpenIcon, StarIcon, XIcon } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useTranslations } from "translations";
import {
  COLUMN_TYPE_SEARCH_TABLE_PREFERRED_INPUT,
  COLUMN_TYPE_SEARCH_TABLE_PREFERRED_INPUT_POTENTIAL_RECEIVERS,
} from ".";
import { CellProps, DefaultTextCell } from "..";
import { DefaultCellBodyText } from "../../styles";

type PatientPreferredInput = {
  id: number;
  patient_preferred: boolean;
  patient_preferred_reason: string | undefined;
};

type PatientPreferredCellRequest =
  | AcceptedTableRequest
  | ContactedTableRequest
  | DeclinedTableRequest
  | PotentialReceiversTableCandidate
  | RejectedTableRequest
  | ValidatedTableRequest;

type StarTranslationType = {
  ariaLabel: string;
  tooltip: string;
};

const useGetStarTranslationType = ({
  careprovider,
  patientPreferred,
  shouldDisable,
}: {
  careprovider: PatientPreferredCellRequest["careprovider"];
  patientPreferred: boolean;
  shouldDisable: boolean;
}): StarTranslationType => {
  const translations = useTranslations();

  if (shouldDisable)
    return {
      ariaLabel:
        translations.search.searchPageTables.accessibility
          .patientWishAccessibility.disabled,
      tooltip: "",
    };

  if (patientPreferred) {
    return {
      ariaLabel:
        translations.search.searchPageTables.accessibility.patientWishAccessibility.checkedStar(
          { providerName: careprovider?.name ?? "" },
        ),
      tooltip:
        translations.search.patientPreferred.tooltipRemovePatientPreferred,
    };
  }
  return {
    ariaLabel:
      translations.search.searchPageTables.accessibility.patientWishAccessibility.uncheckedStar(
        { providerName: careprovider?.name ?? "" },
      ),
    tooltip: translations.search.patientPreferred.markAs,
  };
};

function PatientPreferredIconButton<
  Request extends PatientPreferredCellRequest,
>({
  auctionRequest,
  isPatientPreferred,
  queryProgress,
  setLocalPatientPreferred,
  setPatientPreferred,
  shouldDisable,
}: {
  auctionRequest: Request;
  isPatientPreferred: boolean;
  queryProgress: QueryProgress | undefined;
  setLocalPatientPreferred: React.Dispatch<React.SetStateAction<string>>;
  setPatientPreferred: (value: PatientPreferredInput) => void;
  shouldDisable: boolean;
}) {
  const theme = useTheme();
  const starTranslationType = useGetStarTranslationType({
    careprovider: auctionRequest.careprovider,
    patientPreferred: isPatientPreferred,
    shouldDisable,
  });

  const handleIconClick = () => {
    if (queryProgress === QUERY_PROGRESS_PENDING) return;
    setPatientPreferred({
      id: auctionRequest.id,
      patient_preferred: !isPatientPreferred,
      patient_preferred_reason: undefined,
    });
    setLocalPatientPreferred("");
  };

  return (
    <Tooltip
      placement="bottom"
      title={starTranslationType.tooltip}
      tooltipTextAlign="center"
    >
      <IconButton
        aria-checked={isPatientPreferred}
        aria-label={starTranslationType.ariaLabel}
        data-testid={`patient-preferred-star-${auctionRequest?.careprovider?.id}`}
        disabled={shouldDisable}
        role="checkbox"
        size="small"
        sx={{
          "&:disabled": { color: UNAVAILABLE_GREY },
          color: isPatientPreferred ? theme.palette.secondary.dark : "default",
        }}
        onClick={handleIconClick}
      >
        <StarIcon
          data-testid="preferred-star-icon"
          fill={isPatientPreferred ? theme.palette.secondary.dark : "none"}
          size={FONT_SIZE_16}
        />
      </IconButton>
    </Tooltip>
  );
}

const PreferredReasonInput = ({
  careproviderId,
  hasPreferredReasonUpdated,
  isEditingReason,
  localPatientPreferred,
  patientPreferredReason,
  setEditingReason,
  setLocalPatientPreferred,
  shouldDisable,
}: {
  careproviderId: number;
  hasPreferredReasonUpdated: boolean;
  isEditingReason: boolean;
  localPatientPreferred: string;
  patientPreferredReason: string;
  setEditingReason: React.Dispatch<React.SetStateAction<boolean>>;
  setLocalPatientPreferred: React.Dispatch<React.SetStateAction<string>>;
  shouldDisable: boolean;
}) => {
  const translations = useTranslations();
  // show input field only if there has been no reason entered
  //  or if they are currently editing their reason
  if (!patientPreferredReason || isEditingReason) {
    if (shouldDisable) return null;

    const handleClick = () => {
      if (isEditingReason) {
        return;
      }
      setEditingReason(true);
    };

    const handleBlur = () => {
      setLocalPatientPreferred(localPatientPreferred?.trim());
      // if the reason was edited dont blur field
      // and keep confirmation buttons visible
      if (hasPreferredReasonUpdated) {
        return;
      }
      setEditingReason(false);
    };

    return (
      <TextInputField
        autoFocus={!!patientPreferredReason} // if the user clicks on the field to edit the reason auto focus the field
        elementName={`preferred-text-input-${careproviderId}`}
        inputProps={{
          "aria-label":
            translations.search.searchPageTables.accessibility
              .patientWishAccessibility.addReason,
        }}
        placeholder={translations.search.patientPreferred.reason}
        startAdornment={
          <Tooltip
            title={translations.search.patientPreferred.dataNotEncrypted}
          >
            <LockOpenIcon size={16} style={{ color: ICON_GREY }} />
          </Tooltip>
        }
        style={{ margin: margin(0), width: "auto" }}
        testId={`preferred-text-input-${careproviderId}`}
        value={localPatientPreferred}
        variant="outlined"
        onBlur={handleBlur}
        onChange={setLocalPatientPreferred}
        onClick={handleClick}
      />
    );
  }

  return (
    <DefaultTextCell
      aria-label={
        translations.search.searchPageTables.accessibility
          .patientWishAccessibility.editReason
      }
      cursor={shouldDisable ? "auto" : "pointer"}
      disabled={shouldDisable}
      message={localPatientPreferred}
      testId={`preferred-text-${careproviderId}`}
      width="100%"
      onClick={() => {
        // open input field on click to allow for reason editing
        if (shouldDisable) return;
        setEditingReason(true);
      }}
    />
  );
};

function InputButtons<Request extends PatientPreferredCellRequest>({
  auctionRequest,
  hasPreferredReasonUpdated,
  isEditingReason,
  localPatientPreferred,
  patientPreferredReason,
  setEditingReason,
  setLocalPatientPreferred,
  setPatientPreferred,
}: {
  auctionRequest: Request;
  hasPreferredReasonUpdated: boolean;
  isEditingReason: boolean;
  localPatientPreferred: string;
  patientPreferredReason: string;
  setEditingReason: React.Dispatch<React.SetStateAction<boolean>>;
  setLocalPatientPreferred: React.Dispatch<React.SetStateAction<string>>;
  setPatientPreferred: (value: PatientPreferredInput) => void;
}) {
  const translations = useTranslations();

  const handleClose = () => {
    // if the reason was edited and user cancels editing, return to old reason
    if (hasPreferredReasonUpdated)
      setLocalPatientPreferred(patientPreferredReason ?? "");
    setEditingReason(false);
  };

  const handleConfirm = () => {
    // only send updated reasons to BE
    setEditingReason(false);

    if (hasPreferredReasonUpdated) {
      setPatientPreferred({
        id: auctionRequest.id,
        patient_preferred: true,
        patient_preferred_reason: localPatientPreferred?.length
          ? localPatientPreferred
          : undefined,
      });
    }
  };
  if (!isEditingReason) return null;

  return (
    <VerticalLayout justify="space-between" padding={padding(0.25)}>
      <XIcon
        aria-label={
          translations.search.searchPageTables.accessibility
            .patientWishAccessibility.cancelReason
        }
        data-testid={`preferred-cancel-button-${auctionRequest?.careprovider?.id}`}
        size={16}
        style={{
          backgroundColor: ICON_GREY,
          borderRadius: "100%",
          color: WHITE,
          cursor: "pointer",
          marginRight: dp(4),
        }}
        onClick={handleClose}
      />
      <Tooltip
        title={
          hasPreferredReasonUpdated
            ? ""
            : translations.search.patientPreferred.reasonNotEmpty
        }
      >
        <CheckIcon
          aria-label={
            translations.search.searchPageTables.accessibility
              .patientWishAccessibility.confirmReason
          }
          data-testid={`preferred-confirm-button-${auctionRequest?.careprovider?.id}`}
          size={16}
          style={{
            backgroundColor: hasPreferredReasonUpdated
              ? SUCCESS_COLOR
              : ICON_GREY,
            borderRadius: "100%",
            color: WHITE,
            cursor: hasPreferredReasonUpdated ? "pointer" : "default",
          }}
          onClick={handleConfirm}
        />
      </Tooltip>
    </VerticalLayout>
  );
}

const PatientPreferredCellWrapper = ({
  careproviderId,
  children,
}: {
  careproviderId: number;
  children: React.ReactNode;
}) => {
  return (
    <HorizontalLayout
      data-testid={`patient-preferred-cell-${careproviderId}`}
      style={{
        alignItems: "center",
        columnGap: margin(0.25),
        cursor: "default",
      }}
      width="100%"
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      {children}
    </HorizontalLayout>
  );
};

const PreferredReasonInputWrapper = ({
  children,
  isPatientPreferred,
}: {
  children: React.ReactNode;
  isPatientPreferred: boolean;
}) => {
  if (!isPatientPreferred) return null;
  return <HorizontalLayout style={{ flex: 1 }}>{children}</HorizontalLayout>;
};

export function shouldDisablePatientPreferred({
  auctionStatus,
  patientPreferred,
  table,
}: {
  auctionStatus: number | undefined;
  patientPreferred: boolean;
  table: SearchTable;
}): { shouldDisable: boolean; shouldHide: boolean } {
  const result = { shouldDisable: false, shouldHide: false };
  // all patient actions should be disabled if auction validated or stopped
  if (shouldDisableActions(auctionStatus)) {
    result.shouldDisable = true;
  }
  if (
    !(
      [SEARCH_TABLE_CONTACTED, SEARCH_TABLE_POTENTIAL] as SearchTable[]
    ).includes(table)
  ) {
    // if not in the contacted or potential receivers table patient preferred editing should always be disabled
    result.shouldDisable = true;
    // if not in these tables and not patient preferred dont show anything
    result.shouldHide = !patientPreferred;
  }

  return result;
}

export function PreferredIconInputPresenter<
  Request extends PatientPreferredCellRequest,
>({
  auction,
  auctionRequest,
  isPatientPreferred,
  patientPreferredReason = "",
  queryProgress,
  setPatientPreferred,
  table,
}: {
  auction: Auction;
  auctionRequest: Request;
  isPatientPreferred: boolean;
  patientPreferredReason: string | undefined;
  queryProgress?: QueryProgress;
  setPatientPreferred: (value: PatientPreferredInput) => void;
  table: SearchTable;
}) {
  const [localPatientPreferred, setLocalPatientPreferred] = useSafeState(
    patientPreferredReason,
  );

  useEffect(() => {
    setLocalPatientPreferred(patientPreferredReason);
  }, [patientPreferredReason]);

  const [editingReason, setEditingReason] = useState(false);

  const { shouldDisable, shouldHide } = shouldDisablePatientPreferred({
    auctionStatus: auction.status,
    patientPreferred: isPatientPreferred,
    table,
  });

  if (shouldHide) {
    return null;
  }

  const preferredReasonUpdated =
    (patientPreferredReason?.trim() || "") !==
    (localPatientPreferred?.trim() || "");

  return (
    <PatientPreferredCellWrapper
      careproviderId={auctionRequest?.careprovider?.id || -1}
    >
      <PatientPreferredIconButton
        auctionRequest={auctionRequest}
        isPatientPreferred={isPatientPreferred}
        queryProgress={queryProgress}
        setLocalPatientPreferred={setLocalPatientPreferred}
        setPatientPreferred={setPatientPreferred}
        shouldDisable={shouldDisable}
      />
      <PreferredReasonInputWrapper isPatientPreferred={isPatientPreferred}>
        <PreferredReasonInput
          careproviderId={auctionRequest?.careprovider?.id || -1}
          hasPreferredReasonUpdated={preferredReasonUpdated}
          isEditingReason={editingReason}
          localPatientPreferred={localPatientPreferred}
          patientPreferredReason={patientPreferredReason}
          setEditingReason={setEditingReason}
          setLocalPatientPreferred={setLocalPatientPreferred}
          shouldDisable={shouldDisable}
        />
        <InputButtons
          auctionRequest={auctionRequest}
          hasPreferredReasonUpdated={preferredReasonUpdated}
          isEditingReason={editingReason}
          localPatientPreferred={localPatientPreferred}
          patientPreferredReason={patientPreferredReason}
          setEditingReason={setEditingReason}
          setLocalPatientPreferred={setLocalPatientPreferred}
          setPatientPreferred={setPatientPreferred}
        />
      </PreferredReasonInputWrapper>
    </PatientPreferredCellWrapper>
  );
}

export function PreferredIconInput({
  auction,
  auctionRequest,
  invalidate,
  refresh,
  table,
}: CellProps<typeof COLUMN_TYPE_SEARCH_TABLE_PREFERRED_INPUT>) {
  const isPrint = isPrintMode();
  const [setPatientPreferred, queryProgress] = useRequestAction({
    actionType: REQUEST_ACTION_PATIENT_PREFERRED,
  });

  if (isPrint)
    return (
      <HorizontalLayout>
        {auctionRequest?.patient_preferred && (
          <StarIcon
            size={FONT_SIZE_10}
            style={{
              fontSize: FONT_SIZE_10,
              margin: `auto ${dp(8)} auto ${dp(0)} `,
            }}
          />
        )}
        <DefaultCellBodyText>
          {auctionRequest?.patient_preferred_reason ?? ""}
        </DefaultCellBodyText>
      </HorizontalLayout>
    );

  return (
    <PreferredIconInputPresenter
      auction={auction}
      auctionRequest={auctionRequest}
      isPatientPreferred={!!auctionRequest?.patient_preferred}
      patientPreferredReason={auctionRequest?.patient_preferred_reason}
      queryProgress={queryProgress}
      setPatientPreferred={(props: PatientPreferredInput) =>
        setPatientPreferred({
          auctionRequest: auctionRequest as unknown as AuctionRequest,
          context: props,
          onCompleted: () => invalidate?.(),
          onError: () => refresh?.(),
        })
      }
      table={table}
    />
  );
}

export function PreferredIconInputPotentalReceiver({
  auction,
  auctionRequest,
  table,
}: CellProps<
  typeof COLUMN_TYPE_SEARCH_TABLE_PREFERRED_INPUT_POTENTIAL_RECEIVERS
>) {
  const { getRequestState, setPatientPreferred } =
    usePotentialReceiverContext();
  const requestState = getRequestState(auctionRequest.id);

  return (
    <PreferredIconInputPresenter
      auction={auction}
      auctionRequest={auctionRequest}
      isPatientPreferred={!!requestState?.patient_preferred}
      patientPreferredReason={requestState?.patient_preferred_reason}
      setPatientPreferred={setPatientPreferred}
      table={table}
    />
  );
}
